import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Download, Loader2, LogOut } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/partner/utt/applications`;
const KEY = 'wekeza-partner-key';
const LABELS = { full_name: 'Name', phone: 'Phone', nida: 'NIDA', email: 'Email', dob: 'DOB', address: 'Address', bank_name: 'Bank',
  bank_branch: 'Branch', bank_account: 'Bank acct', nominee_name: 'Nominee', nominee_relationship: 'Relationship', nominee_phone: 'Nominee phone' };
const STATUS_CLS = { submitted: 'bg-sky-50 text-sky-700', needs_info: 'bg-amber-50 text-amber-700', registered: 'bg-emerald-50 text-emerald-700' };

const AppCard = ({ a, headers, onDone }) => {
  const [acct, setAcct] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const act = async (path, body, ok) => {
    setBusy(true);
    try { await axios.post(`${API}/${a.ref}/${path}`, body, { headers }); toast.success(ok); onDone(); }
    catch (e) {
      const d = e.response?.data?.detail;
      toast.error((Array.isArray(d) ? d[0]?.msg : typeof d === 'string' ? d : null) || 'Failed');
    }
    setBusy(false);
  };
  const pdf = async () => {
    const r = await axios.get(`${API}/${a.ref}/pdf`, { headers, responseType: 'blob' }).catch(() => null);
    if (!r) { toast.error('Could not download PDF'); return; }
    const url = URL.createObjectURL(r.data);
    Object.assign(document.createElement('a'), { href: url, download: `${a.ref}.pdf` }).click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`partner-app-${a.ref}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm font-semibold">{a.ref}</span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLS[a.status] || ''}`} data-testid={`partner-app-status-${a.ref}`}>{a.status.replace('_', ' ')}</span>
        <span className="text-xs text-slate-500">{a.fund_name} · {new Date(a.submitted_at).toLocaleString()}</span>
        <button onClick={pdf} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid={`partner-app-pdf-${a.ref}`}><Download className="h-3.5 w-3.5" />PDF</button>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
        {Object.entries(LABELS).map(([k, l]) => (
          <div key={k}><dt className="text-slate-500">{l}</dt><dd className="font-medium text-slate-900 break-all">{a.fields[k] || '—'}</dd></div>
        ))}
      </dl>
      {a.status === 'needs_info' && a.needs_info_reason && (
        <p className="mt-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-800" data-testid={`partner-app-reason-shown-${a.ref}`}>Returned to client: {a.needs_info_reason}</p>
      )}
      {a.status === 'registered' ? (
        <p className="mt-3 text-xs text-emerald-700">UTT account <b>{a.utt_account_number}</b> · {new Date(a.registered_at).toLocaleString()}</p>
      ) : (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <input value={acct} onChange={(e) => setAcct(e.target.value)} placeholder="UTT account number" maxLength={24} className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid={`partner-app-acct-${a.ref}`} />
          <button disabled={busy || acct.trim().length < 4} onClick={() => act('register', { utt_account_number: acct }, 'Registered — client notified')}
            className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid={`partner-app-register-${a.ref}`}>Mark registered</button>
          {a.status === 'submitted' && (
            <>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What is missing?" className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid={`partner-app-reason-${a.ref}`} />
              <button disabled={busy || reason.trim().length < 3} onClick={() => act('needs-info', { reason }, 'Returned to client')}
                className="rounded-lg border border-amber-400 px-4 py-2 text-sm font-semibold text-amber-700 disabled:opacity-50" data-testid={`partner-app-needs-info-${a.ref}`}>Needs info</button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default function PartnerDesk() {
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY) || '');
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('submitted');
  const [rows, setRows] = useState(null);
  const headers = { 'X-Partner-Key': key };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}${status ? `?status=${status}` : ''}`, { headers: { 'X-Partner-Key': key } });
      setRows(r.data.rows);
    } catch {
      toast.error('Invalid partner key');
      sessionStorage.removeItem(KEY);
      setKey('');
    }
  }, [key, status]);
  useEffect(() => { if (key) load(); }, [key, load]);

  if (!key) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <form onSubmit={(e) => { e.preventDefault(); sessionStorage.setItem(KEY, input.trim()); setKey(input.trim()); }}
          className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-sm" data-testid="partner-login">
          <Building2 className="mb-3 h-8 w-8 text-emerald-700" />
          <h1 className="text-lg font-bold">UTT AMIS · CORE Securities Partner Desk</h1>
          <p className="mb-4 text-xs text-slate-500">Applications submitted through Wekeza-Smart.</p>
          <input type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Partner access key"
            className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="partner-key-input" />
          <button className="w-full rounded-lg bg-emerald-700 py-2 text-sm font-semibold text-white" data-testid="partner-login-btn">Open desk</button>
        </form>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-bold">Partner Desk — UTT AMIS applications</h1>
          <button onClick={() => { sessionStorage.removeItem(KEY); setKey(''); }} className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500" data-testid="partner-logout"><LogOut className="h-3.5 w-3.5" />Lock</button>
        </div>
        <div className="flex gap-2">
          {[['submitted', 'New'], ['needs_info', 'Needs info'], ['registered', 'Registered'], ['', 'All']].map(([v, l]) => (
            <button key={l} onClick={() => setStatus(v)} data-testid={`partner-filter-${l.toLowerCase().replace(' ', '-')}`}
              className={`rounded-full px-3 py-1 text-xs font-medium ${status === v ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>{l}</button>
          ))}
        </div>
        {rows === null ? <Loader2 className="h-5 w-5 animate-spin" /> : rows.length === 0
          ? <p className="text-sm text-slate-500" data-testid="partner-empty">No applications.</p>
          : rows.map((a) => <AppCard key={a.ref} a={a} headers={headers} onDone={load} />)}
      </div>
    </div>
  );
}
